/**
 * WhispersTab — renders the Whispers list from a `JournalTabState`.
 *
 * Thin Phaser renderer; the snapshot -> tab-state decision lives in
 * `journalDispatch.buildJournalTabState('whispers', snapshot)`. This
 * module owns nothing but the Phaser objects themselves and a single
 * exported `renderWhispersTab(scene, container, state)` function.
 *
 * F4 upgrade: each whisper now renders as a collapsed preview card
 * (first ~40 chars of body + realm + date dreamed). Click/tap expands
 * to show the full multi-line text. Unread entries pulse with a soft
 * glow on their background panel. Empty state shows the GDD-approved
 * italic cream placeholder.
 *
 * Layout: container is positioned at the journal content column origin
 * by the scene; this renderer lays out lines relative to (0, 0).
 */

import * as Phaser from 'phaser';
import {
  JOURNAL_FONT,
  JOURNAL_INK,
  JOURNAL_INK_MUTED,
  JOURNAL_FONT_SIZE,
  JOURNAL_LAYOUT,
  JOURNAL_PIN,
} from './journalTheme';
import { drawPaperSlip } from '../ui/c5/paperMaterial';
import type { JournalTabState } from './journalDispatch';

/** Max characters shown in the collapsed preview line. */
const PREVIEW_CHAR_LIMIT = 40;

/** Slip styling constants -- each whisper is a pinned paper slip on the
 *  journal spread (C5 "Indigo & Ink", docs/design/ui-c5-indigo-ink.md). */
const SLIP_ALPHA = 0.96;
const PANEL_PAD_X = 12;
const PANEL_PAD_Y = 8;
const PANEL_GAP = 10;

/** Unread mark: a hanko dot in the slip's corner that breathes slowly.
 *  The paper itself no longer pulses -- paper does not flicker. */
const PIN_RADIUS = 4;
const PIN_INSET = 10;
const GLOW_ALPHA_MIN = 0.35;
const GLOW_ALPHA_MAX = 0.95;
const GLOW_DURATION_MS = 1800;

/** Content width available inside the panel. */
const INNER_WIDTH =
  JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 - PANEL_PAD_X * 2;

/** Set of expanded whisper ids — persists across re-renders within the
 *  same JournalScene session so the player doesn't lose their place on
 *  live snapshot refreshes. Cleared on scene shutdown via the scene's
 *  SHUTDOWN event (wired in JournalScene). */
const expandedIds = new Set<string>();

/** Format an epoch-ms timestamp to a quiet date string. */
function formatDate(epochMs: number): string {
  const d = new Date(epochMs);
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

/** Truncate text to ~limit chars at a word boundary, append ellipsis. */
function truncatePreview(text: string, limit: number): string {
  if (text.length <= limit) return text;
  const truncated = text.slice(0, limit);
  const lastSpace = truncated.lastIndexOf(' ');
  const cut = lastSpace > limit * 0.5 ? lastSpace : limit;
  return truncated.slice(0, cut) + '...';
}

/** Render the Whispers tab into `container`. Container is cleared before
 *  the new lines are laid out. */
export function renderWhispersTab(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  state: JournalTabState,
): void {
  container.removeAll(true);

  // Clear expanded set when the scene shuts down so next journal open
  // starts fresh. Wire once via a flag stored in the scene's DataManager.
  const flagKey = '__whispersTabShutdownWired';
  if (!scene.data.get(flagKey)) {
    scene.data.set(flagKey, true);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      expandedIds.clear();
    });
  }

  if (state.lines.length === 0) {
    appendEmptyState(scene, container, state.emptyMessage);
    return;
  }

  let y = 0;
  for (const line of state.lines) {
    const isExpanded = expandedIds.has(line.id);
    const isUnread = line.footnote === 'unread';

    // -- Card container for this entry --
    const cardContainer = scene.add.container(0, y);
    container.add(cardContainer);

    let innerY = PANEL_PAD_Y;

    // Heading: source + realm attribution (muted italic)
    if (line.heading) {
      const heading = scene.add.text(PANEL_PAD_X, innerY, line.heading, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.HEADING,
        color: JOURNAL_INK_MUTED,
        fontStyle: 'italic',
      });
      cardContainer.add(heading);
      innerY += heading.height + 4;
    }

    // Body: collapsed preview or full text
    const bodyText = isExpanded ? line.body : truncatePreview(line.body, PREVIEW_CHAR_LIMIT);
    const body = scene.add.text(PANEL_PAD_X, innerY, bodyText, {
      fontFamily: JOURNAL_FONT,
      fontSize: JOURNAL_FONT_SIZE.BODY,
      color: JOURNAL_INK,
      wordWrap: { width: INNER_WIDTH },
    });
    cardContainer.add(body);
    innerY += body.height + 4;

    // Date dreamed (shown in expanded view, or always if we have the timestamp)
    if (line.unlockedAt && line.unlockedAt > 0) {
      const dateStr = `Dreamed ${formatDate(line.unlockedAt)}`;
      const dateLine = scene.add.text(PANEL_PAD_X, innerY, dateStr, {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.FOOTNOTE,
        color: JOURNAL_INK_MUTED,
        fontStyle: 'italic',
      });
      cardContainer.add(dateLine);
      innerY += dateLine.height + 2;
    }

    // Unread footnote indicator
    if (isUnread && !isExpanded) {
      const unreadLabel = scene.add.text(PANEL_PAD_X, innerY, 'unread', {
        fontFamily: JOURNAL_FONT,
        fontSize: JOURNAL_FONT_SIZE.FOOTNOTE,
        color: JOURNAL_INK_MUTED,
        fontStyle: 'italic',
      });
      cardContainer.add(unreadLabel);
      innerY += unreadLabel.height + 2;
    }

    innerY += PANEL_PAD_Y;

    // -- Background panel (drawn behind text) --
    const panelW = JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2;
    const panelH = innerY;

    const bg = scene.add.graphics();
    drawPaperSlip(bg, 0, 0, panelW, panelH, { radius: 3, alpha: SLIP_ALPHA, shadow: 3 });
    cardContainer.addAt(bg, 0); // behind text

    // -- Unread mark: a hanko dot in the corner, breathing slowly --
    if (isUnread) {
      const pin = scene.add.graphics();
      pin.fillStyle(JOURNAL_PIN, 1);
      pin.fillCircle(panelW - PIN_INSET, PIN_INSET, PIN_RADIUS);
      pin.setAlpha(GLOW_ALPHA_MAX);
      cardContainer.add(pin);
      scene.tweens.add({
        targets: pin,
        alpha: { from: GLOW_ALPHA_MAX, to: GLOW_ALPHA_MIN },
        duration: GLOW_DURATION_MS,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // -- Hit area for expand/collapse --
    const hitZone = scene.add.rectangle(
      panelW / 2, panelH / 2, panelW, panelH, 0x000000, 0,
    );
    hitZone.setInteractive({ useHandCursor: true });
    cardContainer.add(hitZone);

    // Capture id for the closure.
    const lineId = line.id;
    hitZone.on('pointerdown', () => {
      if (expandedIds.has(lineId)) {
        expandedIds.delete(lineId);
      } else {
        expandedIds.add(lineId);
      }
      // Re-render the tab to reflect the change.
      renderWhispersTab(scene, container, state);
    });

    y += panelH + PANEL_GAP;
  }
}

function appendEmptyState(
  scene: Phaser.Scene,
  container: Phaser.GameObjects.Container,
  message: string,
): void {
  const text = scene.add.text(0, 24, message, {
    fontFamily: JOURNAL_FONT,
    fontSize: JOURNAL_FONT_SIZE.EMPTY_STATE,
    color: JOURNAL_INK_MUTED,
    fontStyle: 'italic',
    wordWrap: { width: JOURNAL_LAYOUT.CONTENT_WIDTH - JOURNAL_LAYOUT.CONTENT_PAD_X * 2 },
  });
  container.add(text);
}
